import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";
const MapPage = lazy(() => import("./pages/Map"));
const Survey = lazy(() => import("./pages/Survey"));
import ErrorBoundary from "@/components/ErrorBoundary";
import { MunicipalityProvider, useMunicipalityContext } from "@/contexts/MunicipalityContext";
import { URLConfigProvider, useURLConfigContext } from "@/contexts/URLConfigContext";
import { Screensaver } from "@/components/Screensaver";
import { KioskActivityPrompt } from "@/components/KioskActivityPrompt";
import { useInactivity } from "@/hooks/useInactivity";
import { AndroidOptimizer } from "@/components/AndroidOptimizer";
import { DeviceReloadManager } from "@/components/DeviceReloadManager";
import { useMemoryMonitor } from "@/hooks/useMemoryMonitor";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { setLoggerMunicipalityId, logToRemote } from "@/utils/remoteLogger";

import { TouchCoordinateFixer } from "@/components/TouchCoordinateFixer";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { UI_TRANSLATIONS } from "@/lib/i18n";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 1000,
      staleTime: 2 * 60 * 1000, // 2 minutos (reducido para memoria)
      gcTime: 5 * 60 * 1000, // 5 minutos (reducido)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

// Limpieza periódica de caché cada 5 minutos
setInterval(() => {
  const now = Date.now();
  queryClient.getQueryCache().getAll().forEach((query) => {
    const dataUpdatedAt = query.state.dataUpdatedAt;
    if (dataUpdatedAt && now - dataUpdatedAt > 5 * 60 * 1000) {
      queryClient.removeQueries({ queryKey: query.queryKey });
    }
  });
}, 5 * 60 * 1000);

// Array de eventos de inactividad fuera del componente para mantener referencia estable
const INACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'];

// Componente interno que muestra loading o contenido
const AppLoader = () => {
  const config = useURLConfigContext();

  if (config.isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return <AppContent />;
};

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useURLConfigContext();
  const { language } = useLanguage();
  const translations = UI_TRANSLATIONS[language];
  const { municipalityId, setMunicipalityId } = useMunicipalityContext();
  const [isWebViewOpen, setIsWebViewOpen] = useState(false);

  // Dynamic color variables application
  useEffect(() => {
    if (config.primaryColor) {
      document.documentElement.style.setProperty('--primary-color', config.primaryColor);
    }
    if (config.backgroundColor) {
      document.documentElement.style.setProperty('--background-color', config.backgroundColor);
      document.body.style.backgroundColor = config.backgroundColor;
    }
    
    // El fondo se resuelve en CSS para mantener el acabado editorial del kiosco.
    document.body.style.backgroundImage = 'none';
  }, [config.primaryColor, config.backgroundColor]);

  // Monitoring hooks
  usePerformanceMonitor();

  const memoryStats = useMemoryMonitor({
    checkInterval: 30000,
    warningThreshold: 0.75,
    criticalThreshold: 0.85,
    onWarning: (stats) => {
      logToRemote('memory_warning', {
        usagePercent: Math.round(stats.usagePercent * 100),
        usedMB: Math.round(stats.usedJSHeapSize / 1024 / 1024),
        limitMB: Math.round(stats.jsHeapSizeLimit / 1024 / 1024)
      }, 'warn');
    },
    onCritical: (stats) => {
      logToRemote('memory_critical', {
        usagePercent: Math.round(stats.usagePercent * 100),
        usedMB: Math.round(stats.usedJSHeapSize / 1024 / 1024),
        limitMB: Math.round(stats.jsHeapSizeLimit / 1024 / 1024)
      }, 'critical');

      // Limpiar caché agresivamente
      console.warn('🧹 Limpiando caché por memoria crítica...');
      queryClient.clear();
    },
    enabled: true
  });

  useEffect(() => {
    if (config.municipalityId) {
      setMunicipalityId(config.municipalityId);
    }
  }, [config.municipalityId, setMunicipalityId]);

  // Configurar municipality ID para el logger y loggear app started
  useEffect(() => {
    if (municipalityId) {
      setLoggerMunicipalityId(municipalityId);
      logToRemote('app_started', {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        memory: (performance as any).memory ? {
          limitMB: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
        } : 'not_available'
      }, 'info');
    }
  }, [municipalityId]);

  useEffect(() => {
    const handleWebViewOpen = () => {
      console.log('🌐 WebView abierto - deshabilitando useInactivity');
      setIsWebViewOpen(true);
    };
    const handleWebViewClose = () => {
      console.log('🌐 WebView cerrado - habilitando useInactivity');
      setIsWebViewOpen(false);
    };
    const handleForceScreensaver = () => {
      console.log('💤 Forzando activación del salvapantallas');
      setScreensaverActive(true);
    };

    window.addEventListener('webViewOpen', handleWebViewOpen);
    window.addEventListener('webViewClose', handleWebViewClose);
    window.addEventListener('forceScreensaver', handleForceScreensaver);

    return () => {
      window.removeEventListener('webViewOpen', handleWebViewOpen);
      window.removeEventListener('webViewClose', handleWebViewClose);
      window.removeEventListener('forceScreensaver', handleForceScreensaver);
    };
  }, []);

  const shouldActivateScreensaver = location.pathname === '/';
  const shouldListenForRemoteReload =
    location.pathname === '/' ||
    location.pathname === '/survey' ||
    location.pathname === '/map';

  const { isInactive, resetInactivity } = useInactivity({
    timeout: 30000,
    events: INACTIVITY_EVENTS,
    enabled: shouldActivateScreensaver && !isWebViewOpen,
  });
  const [screensaverActive, setScreensaverActive] = useState(false);
  const [isClosingScreensaver, setIsClosingScreensaver] = useState(false);
  const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);
  const [inactivityPromptSeconds, setInactivityPromptSeconds] = useState(5);

  useEffect(() => {
    if (shouldActivateScreensaver) {
      setScreensaverActive(config.inactivityPromptEnabled ? false : isInactive);
    } else {
      setScreensaverActive(false);
    }
  }, [config.inactivityPromptEnabled, isInactive, shouldActivateScreensaver]);

  useEffect(() => {
    if (
      !config.inactivityPromptEnabled ||
      !shouldActivateScreensaver ||
      !isInactive
    ) {
      setShowInactivityPrompt(false);
      return;
    }

    setShowInactivityPrompt(true);
    setInactivityPromptSeconds(5);

    const countdown = window.setInterval(() => {
      setInactivityPromptSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    const returnHome = window.setTimeout(() => {
      setShowInactivityPrompt(false);
      window.dispatchEvent(new Event('closeWebView'));
      navigate({ pathname: '/', search: window.location.search });
      resetInactivity();
    }, 5000);

    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(returnHome);
    };
  }, [
    config.inactivityPromptEnabled,
    isInactive,
    navigate,
    resetInactivity,
    shouldActivateScreensaver,
  ]);

  const confirmInactivityPrompt = () => {
    setShowInactivityPrompt(false);
    resetInactivity();
  };

  const handleScreensaverClose = () => {
    setIsClosingScreensaver(true);

    // Cerrar cualquier WebView abierto
    window.dispatchEvent(new Event('closeWebView'));

    // Navegar a inicio preservando los query params existentes
    navigate({ pathname: '/', search: window.location.search });

    setTimeout(() => {
      setScreensaverActive(false);
      resetInactivity();
      setIsClosingScreensaver(false);
    }, 300);
  };

  return (
    <div className={screensaverActive || isClosingScreensaver ? 'pointer-events-blocked' : ''}>
      <AndroidOptimizer />
      <TouchCoordinateFixer />
      <DeviceReloadManager
        municipalityId={municipalityId}
        enabled={shouldListenForRemoteReload}
      />
      <Toaster />
      <Sonner />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/map" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">{translations.loading}</div>}>
            <MapPage />
          </Suspense>
        } />
        <Route path="/survey" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">{translations.loading}</div>}>
            <Survey />
          </Suspense>
        } />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {shouldActivateScreensaver && (
        <Screensaver
          municipalityId={municipalityId}
          isActive={screensaverActive}
          onInteraction={handleScreensaverClose}
        />
      )}
      <KioskActivityPrompt
        isVisible={showInactivityPrompt}
        onConfirm={confirmInactivityPrompt}
        remainingSeconds={inactivityPromptSeconds}
      />
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <URLConfigProvider>
          <MunicipalityProvider>
            <TooltipProvider>
              <BrowserRouter>
                <AppLoader />
              </BrowserRouter>
            </TooltipProvider>
          </MunicipalityProvider>
        </URLConfigProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
